import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PaymentsService } from '../../services/payments.service';
import { PrintService } from '../../services/print.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-payment-run-review',
	templateUrl: './payment-run-review.component.html',
	styleUrls: ['./payment-run-review.component.scss']
})
export class PaymentRunReviewComponent implements OnInit, OnDestroy {
	@ViewChild('paymentRunPrintArea', { static: false }) paymentRunPrintArea: ElementRef | null = null;
	private destroy$ = new Subject<void>();

	journal: number | string | null = null;
	run: any = null;
	loading = true;
	error: string | null = null;
	posting = false;
	saving = false;
	successMessage: string | null = null;
	color = 'blue';
	editMode = false;
	editableRemittance: any[] = [];
	summaryCollapsed = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private globalSearchService: GlobalSearchService,
		private paymentsService: PaymentsService,
		private printService: PrintService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(paramMap => {
			const j = paramMap.get('journal');
			if (j) {
				this.journal = j;
				this.loadRun();
			} else {
				this.journal = null;
				this.error = 'Missing run identifier.';
				this.loading = false;
				this.run = null;
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	loadRun(): void {
		if (this.journal == null || this.journal === '') return;
		this.loading = true;
		this.error = null;
		this.editMode = false;
		this.editableRemittance = [];
		const journalParam = typeof this.journal === 'string' && /^\d+$/.test(this.journal) ? parseInt(this.journal, 10) : this.journal;
		this.paymentsService.loadRunData({ journal: journalParam }).subscribe(
			(res: any) => {
				this.loading = false;
				if (res && res.success === true) {
					this.run = {
						success: true,
						journal: res.journal,
						checks: res.checks || [],
						postings: res.postings != null ? res.postings : {},
						fullpostings: res.fullpostings,
						remittance: Array.isArray(res.remittance) ? res.remittance : [],
						savedPayload: res.savedPayload && typeof res.savedPayload === 'object' ? res.savedPayload : this.buildSavedPayloadFromRemittance(res)
					};
					if ((this.run.remittance.length === 0) && this.run.postings && typeof this.run.postings === 'object' && !Array.isArray(this.run.postings)) {
						this.run.remittance = this.remittanceFromPostings(this.run.postings);
					}
					if (!this.run.savedPayload || (Object.keys(this.run.savedPayload.include || {}).length === 0 && this.run.remittance.length > 0)) {
						this.run.savedPayload = this.buildSavedPayloadFromRemittance({ remittance: this.run.remittance, postings: this.run.postings });
					}
				} else {
					this.run = null;
					this.error = (res && res.error) ? res.error : 'Run not found or already posted.';
				}
			},
			(err: any) => {
				this.loading = false;
				this.run = null;
				this.error = err?.error?.error || err?.message || 'Run not found or already posted.';
			}
		);
	}

	private remittanceFromPostings(postings: any): any[] {
		if (!postings || typeof postings !== 'object' || Array.isArray(postings)) return [];
		return Object.keys(postings).map((k: string) => {
			const p = postings[k];
			const allocs = p.Allocs || [];
			return {
				PaytReference: p.PaytReference,
				PayOrder: p.PayOrder,
				PostDate: p.PostDate,
				AccumBalance: p.AccumBalance,
				Currency: p.Currency,
				PaytType: p.PaytType,
				SupplierID: p.SupplierID,
				lines: allocs.map((a: any) => ({
					TransID: a.TransID,
					suppreference: '',
					trandate: '',
					amount: a.Amount,
					discount: 0
				}))
			};
		});
	}

	private buildSavedPayloadFromRemittance(data: { remittance?: any[]; postings?: any; savedPayload?: any; [k: string]: any }): any {
		const remittance = Array.isArray(data.remittance) ? data.remittance : [];
		const include: any = {};
		const payamt: any = {};
		const discamt: any = {};
		const date: any = {};
		for (const rem of remittance) {
			if (rem.SupplierID != null) date[rem.SupplierID] = rem.PostDate || '';
			for (const line of rem.lines || []) {
				const id = line.TransID;
				if (id != null) {
					include[id] = true;
					payamt[id] = line.amount != null ? Number(line.amount) : 0;
					discamt[id] = line.discount != null ? Number(line.discount) : 0;
				}
			}
		}
		const sp = data.savedPayload && typeof data.savedPayload === 'object' ? data.savedPayload : {};
		return {
			include: Object.keys(include).length ? include : (sp.include || {}),
			payamt: Object.keys(payamt).length ? payamt : (sp.payamt || {}),
			discamt: Object.keys(discamt).length ? discamt : (sp.discamt || {}),
			date: Object.keys(date).length ? date : (sp.date || {}),
			bankacct: sp.bankacct != null ? sp.bankacct : '',
			currency: sp.currency != null ? sp.currency : 'USD',
			duedate: sp.duedate != null ? sp.duedate : '',
			checkNum: sp.checkNum != null ? sp.checkNum : '',
			vendorCheckNumbers: sp.vendorCheckNumbers && typeof sp.vendorCheckNumbers === 'object' ? sp.vendorCheckNumbers : {},
			paymentTypes: sp.paymentTypes && typeof sp.paymentTypes === 'object' ? sp.paymentTypes : {}
		};
	}

	enterEditMode(): void {
		const list = this.getRemittanceList();
		this.editableRemittance = list.map((rem: any) => ({
			...rem,
			lines: (rem.lines || []).map((l: any) => ({ ...l, selected: true }))
		}));
		this.editMode = true;
	}

	cancelEdit(): void {
		this.editMode = false;
		this.editableRemittance = [];
	}

	getDisplayRemittance(): any[] {
		return this.editMode ? this.editableRemittance : this.getRemittanceList();
	}

	buildPayloadFromSelection(): any {
		const sp = this.run?.savedPayload;
		if (!sp) return null;
		const include: any = {};
		const payamt: any = {};
		const discamt: any = {};
		const list = this.editMode ? this.editableRemittance : this.getRemittanceList();
		for (const rem of list) {
			for (const line of rem.lines || []) {
				const id = line.TransID;
				const sel = this.editMode ? !!line.selected : true;
				include[id] = sel;
				payamt[id] = sel ? (line.amount || 0) : 0;
				discamt[id] = sel ? (line.discount || 0) : 0;
			}
		}
		return {
			reprint: 'true',
			journal: this.run.journal,
			include,
			payamt,
			discamt,
			date: sp.date || {},
			bankacct: sp.bankacct || '',
			currency: sp.currency || 'USD',
			duedate: sp.duedate || '',
			checkNum: sp.checkNum || '',
			vendorCheckNumbers: sp.vendorCheckNumbers || {},
			paymentTypes: sp.paymentTypes || {}
		};
	}

	buildPostingsFromSelection(): any {
		if (!this.run?.postings || typeof this.run.postings !== 'object') return null;
		const postings: any = {};
		const list = this.editMode ? this.editableRemittance : this.getRemittanceList();
		for (const rem of list) {
			const selectedLines = (rem.lines || []).filter((l: any) => this.editMode ? l.selected : true);
			if (selectedLines.length === 0) continue;
			const key = Object.keys(this.run.postings).find((k: string) =>
				String(this.run.postings[k].SupplierID) === String(rem.SupplierID)
			);
			if (!key) continue;
			const orig = this.run.postings[key];
			const Allocs = selectedLines.map((l: any) => ({
				TransID: l.TransID,
				Amount: (l.amount || 0) + (l.discount || 0)
			}));
			const AccumBalance = selectedLines.reduce((s: number, l: any) => s + (l.amount || 0) + (l.discount || 0), 0);
			const AccumDisc = selectedLines.reduce((s: number, l: any) => s + (l.discount || 0), 0);
			postings[key] = { ...orig, Allocs, AccumBalance, AccumDisc };
		}
		return postings;
	}

	resave(): void {
		const payload = this.buildPayloadFromSelection();
		if (!payload || !this.run?.savedPayload) return;
		this.saving = true;
		this.paymentsService.savePaymentRunForReview(payload).subscribe(
			(res: any) => {
				this.saving = false;
				if (res.success) {
					this.globalSearchService.showNotification('Run updated.', 'success', 'bottom', 'right');
					this.loadRun();
				} else {
					alert(res.error || 'Failed to save.');
				}
			},
			() => {
				this.saving = false;
				alert('Failed to save.');
			}
		);
	}

	canResave(): boolean {
		return !!(this.run?.savedPayload && this.editMode && !this.saving);
	}

	canPostFromEdit(): boolean {
		if (!this.editMode) return true;
		const list = this.editableRemittance;
		const hasAny = list.some((rem: any) => (rem.lines || []).some((l: any) => l.selected));
		return hasAny;
	}

	setVendorSelection(rem: any, selected: boolean): void {
		if (!rem.lines) return;
		rem.lines.forEach((l: any) => { l.selected = selected; });
	}

	allVendorLinesSelected(rem: any): boolean {
		return !!(rem.lines && rem.lines.length && rem.lines.every((l: any) => l.selected));
	}

	setAllLinesSelection(selected: boolean): void {
		this.editableRemittance.forEach((rem: any) => {
			if (rem.lines) rem.lines.forEach((l: any) => { l.selected = selected; });
		});
	}

	allLinesSelected(): boolean {
		const list = this.editableRemittance;
		if (!list.length) return false;
		return list.every((rem: any) => !!(rem.lines && rem.lines.length && rem.lines.every((l: any) => l.selected)));
	}

	getEditableVendorTotal(rem: any): number {
		if (!rem.lines) return 0;
		return rem.lines
			.filter((l: any) => l.selected)
			.reduce((s: number, l: any) => s + (l.amount || 0) + (l.discount || 0), 0);
	}

	approveAndPost(): void {
		if (!this.run?.journal) return;
		const postings = this.editMode ? this.buildPostingsFromSelection() : this.run.postings;
		if (!postings || Object.keys(postings).length === 0) return;
		this.posting = true;
		this.paymentsService.postChecksFinalize({ journal: this.run.journal, postings }).subscribe(
			(res: any) => {
				this.posting = false;
				if (res.success) {
					this.successMessage = 'Payment run posted.';
					this.globalSearchService.showNotification(this.successMessage, 'success', 'bottom', 'right');
					setTimeout(() => this.router.navigate(['/vendors-pending-payment-runs']), 1500);
				} else {
					alert(res.error || 'Failed to post.');
					if (res.error && res.error.indexOf('already posted') !== -1) {
						this.router.navigate(['/vendors-pending-payment-runs']);
					}
				}
			},
			(err: any) => {
				this.posting = false;
				const msg = err?.error?.error || 'Failed to post.';
				alert(msg);
				if (msg.indexOf('already posted') !== -1) {
					this.router.navigate(['/vendors-pending-payment-runs']);
				}
			}
		);
	}

	printPdf(): void {
		if (!this.run?.journal || !this.paymentRunPrintArea?.nativeElement) return;
		const html = this.paymentRunPrintArea.nativeElement.innerHTML;
		if (!html) return;
		const encoded = this.globalSearchService.base64encode(html);
		const filename = `PaymentRun-${this.run.journal}.pdf`;
		this.printService.pdf({
			content: encoded,
			filename,
			landscape: true
		}).subscribe(
			(result: any) => {
				if (result?.content) {
					this.globalSearchService.downloadPdf(result.content, filename);
				} else {
					alert('Could not generate PDF.');
				}
			},
			() => alert('Failed to generate PDF.')
		);
	}

	exportXls(): void {
		if (!this.run?.journal || !this.paymentRunPrintArea?.nativeElement) return;
		const html = this.paymentRunPrintArea.nativeElement.innerHTML;
		if (!html) return;
		const encoded = this.globalSearchService.base64encode(html);
		const filename = `PaymentRun-${this.run.journal}.xlsx`;
		const today = new Date();
		this.printService.xls({
			content: encoded,
			filename,
			title: 'Payment Run',
			subtitle: `Run #${this.run.journal} – ${today.toLocaleString()}`
		}).subscribe(
			(result: any) => {
				if (result?.content) {
					this.globalSearchService.downloadXls(result.content, filename);
				} else {
					alert('Could not generate XLS.');
				}
			},
			() => alert('Failed to generate XLS.')
		);
	}

	backToList(): void {
		this.router.navigate(['/vendors-pending-payment-runs']);
	}

	toggleSummary(): void {
		this.summaryCollapsed = !this.summaryCollapsed;
	}

	getRunSummaryTotal(): number {
		const list = this.getDisplayRemittance();
		return list.reduce((sum: number, rem: any) => sum + (this.editMode ? this.getEditableVendorTotal(rem) : (rem.AccumBalance || 0)), 0);
	}

	getRunSummaryDiscount(): number {
		const list = this.getDisplayRemittance();
		let d = 0;
		list.forEach((rem: any) => {
			(rem.lines || []).forEach((l: any) => {
				if (!this.editMode || l.selected) d += l.discount || 0;
			});
		});
		return d;
	}

	getRunSummaryTransactionCount(): number {
		const list = this.getDisplayRemittance();
		return list.reduce((n: number, rem: any) => n + (this.editMode ? (rem.lines || []).filter((l: any) => l.selected).length : (rem.lines || []).length), 0);
	}

	getRunSummaryCurrency(): string {
		const list = this.getRemittanceList();
		return (list[0] && list[0].Currency) || this.run?.savedPayload?.currency || 'USD';
	}

	getRunSummaryVendorCount(): number {
		return this.getDisplayRemittance().length;
	}

	getVendorTotal(rem: any): number {
		return this.editMode ? this.getEditableVendorTotal(rem) : (rem.AccumBalance || 0);
	}

	getRemittanceList(): any[] {
		if (!this.run) return [];
		if (Array.isArray(this.run.remittance) && this.run.remittance.length > 0) return this.run.remittance;
		const postings = this.run.postings;
		if (postings && typeof postings === 'object' && !Array.isArray(postings)) {
			return Object.keys(postings).map((k: string) => {
				const p = postings[k];
				const allocs = p.Allocs || [];
				return {
					PaytReference: p.PaytReference,
					PayOrder: p.PayOrder,
					PostDate: p.PostDate,
					AccumBalance: p.AccumBalance,
					Currency: p.Currency,
					PaytType: p.PaytType,
					SupplierID: p.SupplierID,
					lines: allocs.map((a: any) => ({
						TransID: a.TransID,
						suppreference: '',
						trandate: '',
						amount: a.Amount,
						discount: 0
					}))
				};
			});
		}
		return [];
	}
}
